import {
  EncryptionPublicKey,
  EncryptionPrivateKey,
  AsymEncryptedBlob,
  asymEncrypt,
  asymDecrypt,
  loadEncryptionPrivateKey,
  loadAsymEncryptedBlob,
  serializeEncryptionPrivateKey,
  serializeEncryptionPublicKey,
} from './lowlevel/asymmetric';
import { Hash, hashData, hashEqual } from './lowlevel/hashing';
import { serializeSharedSecretPublicKey, SharedSecretPubKey } from './lowlevel/sharedsecret';
import {
  Signature,
  SigningPrivateKey,
  SigningPublicKey,
  sign,
  verifySignature,
  loadSigningPrivateKey,
  loadSignature,
  serializeSigningPrivateKey,
  serializeSigningPublicKey,
} from './lowlevel/signing';
import {
  SymEncryptionKey,
  symEncrypt,
  symDecrypt,
  SymEncryptedBlob,
  loadSymEncryptedBlob,
  loadSymEncryptionKey,
} from './lowlevel/symmetric';
import { SALT_SIG_SIGNING, SALT_SIG_ENCRYPTION, SALT_SIG_HASH, SALT_SIG_SHARED } from './salts';
import { Brand } from './types';
import { ArrayOrBase64, loadArrayOrBase64 } from './utils';

export type SymEncryptedEncryptionPrivateKey = Brand<SymEncryptedBlob, 'EncryptionPrivateKey'>;
export type SymEncryptedSigningPrivateKey = Brand<SymEncryptedBlob, 'SigningPrivateKey'>;
export type SymEncryptedSymmetricEncryptionKey = Brand<SymEncryptedBlob, 'SymmetricEncryptionKey'>;
export type EncryptionPublicKeySignature = Brand<Signature, 'EncryptionPublicKey'>;
export type SigningPublicKeySignature = Brand<Signature, 'SigningPublicKey'>;
export type SharedSecretPubKeySignature = Brand<Signature, 'SharedSecretPubKey'>;
export type HashSignature = Brand<Signature, 'Hash'>;
export type AsymEncryptedSymmetricKey = Brand<AsymEncryptedBlob, 'SymmetricKey'>;
export type AsymEncryptedEncryptionPrivateKey = Brand<AsymEncryptedBlob, 'EncryptionPrivateKey'>;

export function loadAsymEncryptedEncryptionPrivateKey(
  data: ArrayOrBase64,
): AsymEncryptedEncryptionPrivateKey {
  return loadArrayOrBase64(data) as AsymEncryptedEncryptionPrivateKey;
}

export function loadSymEncryptedSigningPrivateKey(
  data: ArrayOrBase64,
): SymEncryptedSigningPrivateKey {
  return loadSymEncryptedBlob(data) as SymEncryptedSigningPrivateKey;
}

export function loadSymEncryptedEncryptionPrivateKey(
  data: ArrayOrBase64,
): SymEncryptedEncryptionPrivateKey {
  return loadSymEncryptedBlob(data) as SymEncryptedEncryptionPrivateKey;
}

export function loadSymEncryptedSymmetricEncryptionKey(
  data: ArrayOrBase64,
): SymEncryptedSymmetricEncryptionKey {
  return loadSymEncryptedBlob(data) as SymEncryptedSymmetricEncryptionKey;
}

export function loadEncryptionPublicKeySignature(
  data: ArrayOrBase64,
): EncryptionPublicKeySignature {
  return loadSignature(data) as EncryptionPublicKeySignature;
}

export function loadSigningPublicKeySignature(data: ArrayOrBase64): SigningPublicKeySignature {
  return loadSignature(data) as SigningPublicKeySignature;
}

export function loadAsymEncryptedSymmetricKey(data: ArrayOrBase64): AsymEncryptedSymmetricKey {
  return loadAsymEncryptedBlob(data) as AsymEncryptedSymmetricKey;
}

export async function symEncryptEncryptionPrivateKey(
  symKey: SymEncryptionKey,
  encryptionPrivateKey: EncryptionPrivateKey,
): Promise<SymEncryptedEncryptionPrivateKey> {
  const encPrivKeyEncrypted = symEncrypt(
    symKey,
    await serializeEncryptionPrivateKey(encryptionPrivateKey),
  );
  return encPrivKeyEncrypted as SymEncryptedEncryptionPrivateKey;
}

export async function symDecryptEncryptionPrivateKey(
  symKey: SymEncryptionKey,
  encrypedKey: SymEncryptedEncryptionPrivateKey,
): Promise<EncryptionPrivateKey> {
  return loadEncryptionPrivateKey(symDecrypt(symKey, encrypedKey));
}

export async function symEncryptSigningPrivateKey(
  symKey: SymEncryptionKey,
  sigPrivKey: SigningPrivateKey,
): Promise<SymEncryptedSigningPrivateKey> {
  const sigPrivKeyEncrypted = symEncrypt(symKey, await serializeSigningPrivateKey(sigPrivKey));
  return sigPrivKeyEncrypted as SymEncryptedSigningPrivateKey;
}

export async function symDecryptSigningPrivateKey(
  symKey: SymEncryptionKey,
  encrPrivkey: SymEncryptedSigningPrivateKey,
): Promise<SigningPrivateKey> {
  return loadSigningPrivateKey(symDecrypt(symKey, encrPrivkey));
}

export async function symEncryptSymmetricEncryptionKey(
  symKey: SymEncryptionKey,
  symKeyToEncrypt: SymEncryptionKey,
): Promise<SymEncryptedSymmetricEncryptionKey> {
  const symKeyEncrypted = symEncrypt(symKey, symKeyToEncrypt);
  return symKeyEncrypted as SymEncryptedSymmetricEncryptionKey;
}

export async function symDecryptSymmetricEncryptionKey(
  symKey: SymEncryptionKey,
  symEncryptedSymKey: SymEncryptedSymmetricEncryptionKey,
): Promise<SymEncryptionKey> {
  return loadSymEncryptionKey(symDecrypt(symKey, symEncryptedSymKey));
}

export async function asymCreateKek(
  asymKey: EncryptionPublicKey,
  symKey: SymEncryptionKey,
): Promise<AsymEncryptedSymmetricKey> {
  const asymEncryptedBlob = await asymEncrypt(asymKey, symKey);
  return loadAsymEncryptedSymmetricKey(asymEncryptedBlob);
}

export async function asymDecryptKek(
  asymKey: EncryptionPrivateKey,
  kek: AsymEncryptedSymmetricKey,
): Promise<SymEncryptionKey> {
  const symKey = await asymDecrypt(asymKey, kek);
  return loadSymEncryptionKey(symKey);
}

export async function asymEncryptEncryptionPrivateKey(
  encryptionKey: EncryptionPublicKey,
  payloadKey: EncryptionPrivateKey,
): Promise<AsymEncryptedEncryptionPrivateKey> {
  const asymEncryptedBlob = await asymEncrypt(
    encryptionKey,
    await serializeEncryptionPrivateKey(payloadKey),
  );
  return loadAsymEncryptedEncryptionPrivateKey(asymEncryptedBlob);
}

export async function asymDecryptEncryptionPrivateKey(
  decryptionKey: EncryptionPrivateKey,
  encryptedKey: AsymEncryptedEncryptionPrivateKey,
): Promise<EncryptionPrivateKey> {
  const decryptedKey = await asymDecrypt(decryptionKey, encryptedKey);
  return loadEncryptionPrivateKey(decryptedKey);
}

export async function signEncryptionPublicKey(
  sigPrivKey: SigningPrivateKey,
  encPubKey: EncryptionPublicKey,
  salt: string,
): Promise<EncryptionPublicKeySignature> {
  const encPubKeySignature = sign(
    sigPrivKey,
    await serializeEncryptionPublicKey(encPubKey),
    SALT_SIG_ENCRYPTION + salt,
  );
  return encPubKeySignature as EncryptionPublicKeySignature;
}

export async function verifySignedEncryptionPublicKey(
  verifyingKey: SigningPublicKey,
  encPubKey: EncryptionPublicKey,
  encPubKeySignature: EncryptionPublicKeySignature,
  salt: string,
): Promise<boolean> {
  return verifySignature(
    verifyingKey,
    encPubKeySignature,
    await serializeEncryptionPublicKey(encPubKey),
    SALT_SIG_ENCRYPTION + salt,
  );
}

export async function signSigningPublicKey(
  sigPrivKey: SigningPrivateKey,
  sigPubKey: SigningPublicKey,
  salt: string,
): Promise<SigningPublicKeySignature> {
  const sigPubKeySignature = sign(
    sigPrivKey,
    await serializeSigningPublicKey(sigPubKey),
    SALT_SIG_SIGNING + salt,
  );
  return sigPubKeySignature as SigningPublicKeySignature;
}

export async function verifySignedSigningPublicKey(
  verifyingSigPubKey: SigningPublicKey,
  sigPubKey: SigningPublicKey,
  sigPubKeySignature: SigningPublicKeySignature,
  salt: string,
): Promise<boolean> {
  return verifySignature(
    verifyingSigPubKey,
    sigPubKeySignature,
    await serializeSigningPublicKey(sigPubKey),
    SALT_SIG_SIGNING + salt,
  );
}

export async function signSharedSecretPubKey(
  sigPrivKey: SigningPrivateKey,
  sharedPubKey: SharedSecretPubKey,
  salt: string,
): Promise<SharedSecretPubKeySignature> {
  const sigPubKeySignature = await sign(
    sigPrivKey,
    await serializeSharedSecretPublicKey(sharedPubKey),
    SALT_SIG_SHARED + salt,
  );
  return sigPubKeySignature as SharedSecretPubKeySignature;
}

export async function verifySharedSecretPubKey(
  verifyingSigPubKey: SigningPublicKey,
  sharedPubKey: SharedSecretPubKey,
  sharedPubKeySignature: SharedSecretPubKeySignature,
  salt: string,
): Promise<boolean> {
  return verifySignature(
    verifyingSigPubKey,
    sharedPubKeySignature,
    await serializeSharedSecretPublicKey(sharedPubKey),
    SALT_SIG_SHARED + salt,
  );
}

export async function signHash(signKey: SigningPrivateKey, hash: Hash): Promise<HashSignature> {
  const hashSig = await sign(signKey, hash, SALT_SIG_HASH);
  return hashSig as HashSignature;
}

export async function hashAndSign(
  signKey: SigningPrivateKey,
  data: Uint8Array<ArrayBuffer>,
): Promise<HashSignature> {
  const hash = await hashData(data);
  return signHash(signKey, hash);
}

export async function verifyHashSignature(
  verifyingKey: SigningPublicKey,
  signature: HashSignature,
  hash: Hash,
): Promise<boolean> {
  return verifySignature(verifyingKey, signature, hash, SALT_SIG_HASH);
}

export async function verifyHashedAndSignedData(
  verifyingKey: SigningPublicKey,
  signature: HashSignature,
  data: Uint8Array<ArrayBuffer>,
): Promise<boolean> {
  const hash = await hashData(data);
  return verifyHashSignature(verifyingKey, signature, hash);
}

export async function hashEncryptionPublicKey(pubKey: SharedSecretPubKey): Promise<Hash> {
  const data = await serializeSharedSecretPublicKey(pubKey);
  return hashData(data);
}

export async function verifyEncryptionPublicKeyByHash(
  pubKey: SharedSecretPubKey,
  hash: Hash,
): Promise<boolean> {
  const data = await serializeSharedSecretPublicKey(pubKey);
  const hashedKey = await hashData(data);
  return hashEqual(hashedKey, hash);
}
